import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Star, Settings, History, ChevronRight, X, ShoppingCart } from 'lucide-react'
import { api } from '../lib/api'
import { formatDate } from '../lib/utils'
import { useAuth } from '../context/AuthContext'

interface LoyaltySettings {
  earn_rate_per_jod: string
  abandoned_cart_delay_minutes: string
  drops_review_reward?: string
  drops_order_rating_reward?: string
}

interface PointsTransaction {
  id: string
  type: string
  amount: number
  description: string | null
  order_id: string | null
  created_at: string
}

const TX_TYPE_LABELS: Record<string, string> = {
  earn_order:    'Order reward',
  earn_referral: 'Referral bonus',
  earn_review:   'Review reward',
  earn_share:    'Share reward',
  redeem:        'Redeemed',
  admin_adjust:  'Admin adjustment',
}

function TransactionsPanel({ customerId, customerName, onClose }: {
  customerId: string
  customerName: string
  onClose: () => void
}) {
  const [adjustAmount, setAdjustAmount] = useState('')
  const [adjustNote, setAdjustNote] = useState('')
  const [adjustError, setAdjustError] = useState('')
  const qc = useQueryClient()

  const { data, isLoading } = useQuery<{ transactions: PointsTransaction[] }>({
    queryKey: ['points-tx', customerId],
    queryFn: () => api.get(`/loyalty/customers/${customerId}/transactions`).then(r => r.data),
  })

  const adjust = useMutation({
    mutationFn: ({ amount, description }: { amount: number; description: string }) =>
      api.post(`/loyalty/customers/${customerId}/adjust`, { amount, description }).then(r => r.data),
    onSuccess: () => {
      setAdjustAmount('')
      setAdjustNote('')
      setAdjustError('')
      qc.invalidateQueries({ queryKey: ['points-tx', customerId] })
      qc.invalidateQueries({ queryKey: ['customers'] })
    },
    onError: (e: any) => setAdjustError(e.response?.data?.message ?? 'Failed to adjust points'),
  })

  const handleAdjust = () => {
    const n = parseInt(adjustAmount, 10)
    if (isNaN(n) || n === 0) { setAdjustError('Enter a non-zero integer'); return }
    adjust.mutate({ amount: n, description: adjustNote.trim() || 'Admin adjustment' })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/40" onClick={onClose}>
      <div
        className="h-full w-full max-w-md bg-[#1a1a1a] border-l border-white/10 flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <p className="text-xs text-gray-400">Points history</p>
            <p className="font-semibold text-white">{customerName}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Manual adjust */}
        <div className="px-6 py-4 border-b border-white/10">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Manual adjustment</p>
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="e.g. 50 or -20"
              value={adjustAmount}
              onChange={e => setAdjustAmount(e.target.value)}
              className="w-28 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
            />
            <input
              type="text"
              placeholder="Reason (optional)"
              value={adjustNote}
              onChange={e => setAdjustNote(e.target.value)}
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
            />
            <button
              onClick={handleAdjust}
              disabled={adjust.isPending}
              className="px-3 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50"
            >
              Apply
            </button>
          </div>
          {adjustError && <p className="text-red-400 text-xs mt-1">{adjustError}</p>}
        </div>

        {/* Transaction list */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-40 text-gray-500 text-sm">Loading…</div>
          ) : !data?.transactions.length ? (
            <div className="flex items-center justify-center h-40 text-gray-500 text-sm">No transactions yet</div>
          ) : (
            <ul className="divide-y divide-white/5">
              {data.transactions.map(tx => (
                <li key={tx.id} className="flex items-start gap-3 px-6 py-3">
                  <div className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${tx.amount > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {tx.amount > 0 ? '+' : ''}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white">{TX_TYPE_LABELS[tx.type] ?? tx.type}</p>
                    {tx.description && <p className="text-xs text-gray-400 truncate">{tx.description}</p>}
                    <p className="text-xs text-gray-500">{formatDate(tx.created_at)}</p>
                  </div>
                  <span className={`text-sm font-bold shrink-0 ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount} pts
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

export default function Loyalty() {
  const { user } = useAuth()
  const canManage = user?.role === 'super_admin' || user?.role === 'operations'
  const qc = useQueryClient()
  const [panelCustomerId, setPanelCustomerId] = useState<string | null>(null)
  const [panelCustomerName, setPanelCustomerName] = useState('')
  const [earnRate, setEarnRate] = useState('')
  const [earnError, setEarnError] = useState('')
  const [earnSaved, setEarnSaved] = useState(false)
  const [cartDelay, setCartDelay] = useState('')
  const [cartError, setCartError] = useState('')
  const [cartSaved, setCartSaved] = useState(false)
  const [reviewReward, setReviewReward] = useState('')
  const [reviewRewardError, setReviewRewardError] = useState('')
  const [reviewRewardSaved, setReviewRewardSaved] = useState(false)
  const [ratingReward, setRatingReward] = useState('')
  const [ratingRewardError, setRatingRewardError] = useState('')
  const [ratingRewardSaved, setRatingRewardSaved] = useState(false)

  const { data: settingsData, isLoading: settingsLoading } = useQuery<{ settings: LoyaltySettings }>({
    queryKey: ['loyalty-settings'],
    queryFn: () => api.get('/loyalty/settings').then(r => r.data),
    onSuccess: (d: { settings: LoyaltySettings }) => {
      if (!earnRate) setEarnRate(d.settings.earn_rate_per_jod ?? '10')
      if (!cartDelay) setCartDelay(d.settings.abandoned_cart_delay_minutes ?? '60')
      if (!reviewReward) setReviewReward(d.settings.drops_review_reward ?? '5')
      if (!ratingReward) setRatingReward(d.settings.drops_order_rating_reward ?? '3')
    },
  } as any)

  const { data: customersData } = useQuery<{ customers: any[]; total: number }>({
    queryKey: ['customers'],
    queryFn: () => api.get('/customers?limit=50').then(r => r.data),
  })

  const patchSettings = (payload: Record<string, number>) =>
    api.patch('/loyalty/settings', payload).then(r => r.data)

  const saveEarnRate = useMutation({
    mutationFn: (earn_rate_per_jod: number) => patchSettings({ earn_rate_per_jod }),
    onSuccess: () => {
      setEarnError('')
      setEarnSaved(true)
      setTimeout(() => setEarnSaved(false), 2000)
      qc.invalidateQueries({ queryKey: ['loyalty-settings'] })
    },
    onError: (e: any) => setEarnError(e.response?.data?.message ?? 'Failed to save'),
  })

  const saveCartDelay = useMutation({
    mutationFn: (abandoned_cart_delay_minutes: number) => patchSettings({ abandoned_cart_delay_minutes }),
    onSuccess: () => {
      setCartError('')
      setCartSaved(true)
      setTimeout(() => setCartSaved(false), 2000)
      qc.invalidateQueries({ queryKey: ['loyalty-settings'] })
    },
    onError: (e: any) => setCartError(e.response?.data?.message ?? 'Failed to save'),
  })

  const saveReviewReward = useMutation({
    mutationFn: (drops_review_reward: number) => patchSettings({ drops_review_reward }),
    onSuccess: () => {
      setReviewRewardError('')
      setReviewRewardSaved(true)
      setTimeout(() => setReviewRewardSaved(false), 2000)
      qc.invalidateQueries({ queryKey: ['loyalty-settings'] })
    },
    onError: (e: any) => setReviewRewardError(e.response?.data?.message ?? 'Failed to save'),
  })

  const saveRatingReward = useMutation({
    mutationFn: (drops_order_rating_reward: number) => patchSettings({ drops_order_rating_reward }),
    onSuccess: () => {
      setRatingRewardError('')
      setRatingRewardSaved(true)
      setTimeout(() => setRatingRewardSaved(false), 2000)
      qc.invalidateQueries({ queryKey: ['loyalty-settings'] })
    },
    onError: (e: any) => setRatingRewardError(e.response?.data?.message ?? 'Failed to save'),
  })

  const handleSaveEarnRate = () => {
    const n = parseInt(earnRate, 10)
    if (isNaN(n) || n < 0) { setEarnError('Must be a non-negative integer'); return }
    saveEarnRate.mutate(n)
  }

  const handleSaveCartDelay = () => {
    const n = parseInt(cartDelay, 10)
    if (isNaN(n) || n < 1) { setCartError('Must be at least 1 minute'); return }
    saveCartDelay.mutate(n)
  }

  const handleSaveReviewReward = () => {
    const n = parseInt(reviewReward, 10)
    if (isNaN(n) || n < 0) { setReviewRewardError('Must be a non-negative integer'); return }
    saveReviewReward.mutate(n)
  }

  const handleSaveRatingReward = () => {
    const n = parseInt(ratingReward, 10)
    if (isNaN(n) || n < 0) { setRatingRewardError('Must be a non-negative integer'); return }
    saveRatingReward.mutate(n)
  }

  const currentRate = settingsData?.settings.earn_rate_per_jod ?? earnRate

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">
      <h1 className="text-2xl font-bold text-white">Loyalty & Points</h1>

      {/* Earn settings card */}
      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 p-6">
        <div className="flex items-center gap-2 mb-5">
          <Settings size={16} className="text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Earn Settings</h2>
        </div>

        {settingsLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Points earned per JOD spent</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  value={earnRate || currentRate}
                  onChange={e => setEarnRate(e.target.value)}
                  disabled={!canManage}
                  className="w-24 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a] disabled:opacity-50"
                />
                <span className="text-sm text-gray-400">pts / JOD</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                e.g. order of JD 18.500 → {Math.floor(18.5 * parseInt(earnRate || currentRate || '10', 10))} pts
              </p>
            </div>
            {canManage && (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveEarnRate}
                  disabled={saveEarnRate.isPending}
                  className="px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50"
                >
                  {earnSaved ? '✓ Saved' : 'Save'}
                </button>
                {earnError && <p className="text-red-400 text-xs">{earnError}</p>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Abandoned cart card */}
      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 p-6">
        <div className="flex items-center gap-2 mb-5">
          <ShoppingCart size={16} className="text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Abandoned Cart</h2>
        </div>

        {settingsLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Notification delay</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  value={cartDelay || settingsData?.settings.abandoned_cart_delay_minutes || '60'}
                  onChange={e => setCartDelay(e.target.value)}
                  disabled={!canManage}
                  className="w-24 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a] disabled:opacity-50"
                />
                <span className="text-sm text-gray-400">minutes</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Push notification sent when a cart sits idle for this long with no order placed.
              </p>
            </div>
            {canManage && (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveCartDelay}
                  disabled={saveCartDelay.isPending}
                  className="px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50"
                >
                  {cartSaved ? '✓ Saved' : 'Save'}
                </button>
                {cartError && <p className="text-red-400 text-xs">{cartError}</p>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Drops rewards card — first-review-per-product + order-rating payouts */}
      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 p-6">
        <div className="flex items-center gap-2 mb-5">
          <Star size={16} className="text-yellow-400" />
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Drops Rewards</h2>
        </div>

        {settingsLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <div className="space-y-6">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Product review reward</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  value={reviewReward || settingsData?.settings.drops_review_reward || '5'}
                  onChange={e => setReviewReward(e.target.value)}
                  disabled={!canManage}
                  className="w-24 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a] disabled:opacity-50"
                />
                <span className="text-sm text-gray-400">drops</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Paid the first time a customer reviews each product. Set to 0 to disable.
              </p>
              {canManage && (
                <div className="flex items-center gap-3 mt-2">
                  <button
                    onClick={handleSaveReviewReward}
                    disabled={saveReviewReward.isPending}
                    className="px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50"
                  >
                    {reviewRewardSaved ? '✓ Saved' : 'Save'}
                  </button>
                  {reviewRewardError && <p className="text-red-400 text-xs">{reviewRewardError}</p>}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-1">Order rating reward</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  value={ratingReward || settingsData?.settings.drops_order_rating_reward || '3'}
                  onChange={e => setRatingReward(e.target.value)}
                  disabled={!canManage}
                  className="w-24 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a] disabled:opacity-50"
                />
                <span className="text-sm text-gray-400">drops</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Paid when a customer rates an order (1–5 stars). One payout per order. Set to 0 to disable.
              </p>
              {canManage && (
                <div className="flex items-center gap-3 mt-2">
                  <button
                    onClick={handleSaveRatingReward}
                    disabled={saveRatingReward.isPending}
                    className="px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50"
                  >
                    {ratingRewardSaved ? '✓ Saved' : 'Save'}
                  </button>
                  {ratingRewardError && <p className="text-red-400 text-xs">{ratingRewardError}</p>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Customer balances */}
      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
        <div className="flex items-center gap-2 px-6 py-4 border-b border-white/10">
          <Star size={16} className="text-yellow-400" />
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Customer Balances</h2>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-white/5">
              <th className="px-6 py-3 font-medium">Customer</th>
              <th className="px-6 py-3 font-medium">Phone</th>
              <th className="px-6 py-3 font-medium text-right">Balance</th>
              <th className="px-6 py-3 font-medium text-right">Orders</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {(customersData?.customers ?? [])
              .sort((a, b) => b.loyalty_points - a.loyalty_points)
              .map((c) => (
                <tr key={c.id} className="hover:bg-white/5 transition-colors">
                  <td className="px-6 py-3 text-white font-medium">{c.full_name || '—'}</td>
                  <td className="px-6 py-3 text-gray-400">{c.phone}</td>
                  <td className="px-6 py-3 text-right">
                    <span className="inline-flex items-center gap-1 font-semibold text-yellow-400">
                      ⭐ {c.loyalty_points.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-right text-gray-400">{c.order_count}</td>
                  <td className="px-6 py-3 text-right">
                    <button
                      onClick={() => { setPanelCustomerId(c.id); setPanelCustomerName(c.full_name || c.phone) }}
                      className="inline-flex items-center gap-1 text-xs text-[#c41e3a] hover:text-white transition-colors"
                    >
                      <History size={13} /> History <ChevronRight size={13} />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {!customersData?.customers.length && (
          <p className="px-6 py-8 text-center text-sm text-gray-500">No customers yet</p>
        )}
      </div>

      {panelCustomerId && (
        <TransactionsPanel
          customerId={panelCustomerId}
          customerName={panelCustomerName}
          onClose={() => setPanelCustomerId(null)}
        />
      )}
    </div>
  )
}
